import fs from "node:fs/promises";
import { homedir } from "node:os";
import path from "node:path";
import type { EditorPreferencesPort } from "../ports.js";

function getZuploConfigDir(): string {
  const xdgConfigHome =
    process.env.XDG_CONFIG_HOME ?? path.join(homedir(), ".config");
  return path.join(xdgConfigHome, "zuplo");
}

function getEditorConfigPath(): string {
  return path.join(getZuploConfigDir(), "editor.json");
}

/**
 * Stores the user's external-editor choice in `~/.config/zuplo/editor.json`, so
 * it survives across CLI runs.
 *
 * This is deliberately outside the project directory and therefore outside
 * `FileSystemPort` — it's a per-user setting, not part of the API being edited.
 */
export function createNodeEditorPreferences(): EditorPreferencesPort {
  return {
    async get(): Promise<string | null> {
      try {
        const data = await fs.readFile(getEditorConfigPath(), "utf-8");
        return (JSON.parse(data).editor as string | undefined) ?? null;
      } catch {
        // No file yet, or something unparseable in it — either way the user has
        // no usable preference, and failing the request would block the editor
        // from loading over a cosmetic setting.
        return null;
      }
    },

    async set(editor: string): Promise<void> {
      await fs.mkdir(getZuploConfigDir(), { recursive: true });
      await fs.writeFile(
        getEditorConfigPath(),
        JSON.stringify({ editor }, null, 2),
        "utf-8",
      );
    },
  };
}
